import { ChevronRightIcon } from '@primer/octicons-react';
import { Box } from '@primer/react';
import { isEnoughAccessToReadOutput, isEnoughAccessToReadWithoutWeights } from '@upright/common/iam/access/types';
import { FinalImpactScoreType, toImpactScoreIVKey } from '@upright/common/model/output/scores/types';
import { Valence } from '@upright/common/model/types';
import { friendlyShortImpactTitle } from '@upright/common/utils';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { Link } from '@upright/ui/common/generic/components/primer-custom/link';
import { useOnClickOutside } from '@upright/ui/common/generic/hooks';
import { valenceTextColorPrimer } from '@upright/ui/common/generic/utilities/format';
import { GenericDynamicHeightInfoBox } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/generic-dynamic-height-info-box';
import { InfoBoxBottomPart } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-bottom-part';
import { InfoBoxTitleRow } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-title-row';
import { commonInfoBoxPadding } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/styles';
import { LensBoxMode } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { valenceToSide } from '@upright/ui/common/upright/core/impact-lens-profile/common/utils';
import { useCarouselContext, useCarouselLayout } from '@upright/ui/common/upright/core/impact-lens-profile/context';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { SingleImpactContributors } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/contributors';
import { signForValenceAndValue } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/shared';
import { formatImpactScore } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-formatter';
import { useModelEntityDrilldownUrl } from '@upright/ui/untidy/urls';
import { isNil } from 'lodash';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { formatPercentileIntoTopPercentByValence } from '../../common/utils';
import { NoDrillDownAccess } from './no-drilldown-access';
import { FundBenchmarkUniverseCount } from '@upright/ui/views/model-entity-views/groupfolio-view/histogram-block/summary';

export function SimpleImpactDetailsInfoBox() {
  const context = useNetImpactLensContext();
  const layout = useCarouselLayout();
  const [showNoAccess, setShowNoAccess] = useState(false);

  const ref = useClearStateOnOutsideClickRef(() => {
    setShowNoAccess(false);
  });
  const pushGtmEvent = usePushGtmEvent();
  const getDrilldownUrl = useModelEntityDrilldownUrl();

  if (!context) {
    return <></>;
  }

  const data = context.data;
  const selection = context.state;
  const valence = selection.valence;
  const impact = selection.impact;
  const scores = data?.scores;
  const formatters = context.options.formatters;
  const entity = data?.entity;
  const modelResultId = data?.modelResultId;
  const scoreReader = context.options.scoreReader;

  const value = scoreReader.readScore(impact, undefined, undefined, valence);

  if (isNil(value) || !entity || !scores || !impact || !valence) {
    return <></>;
  }

  const percentileMap = data.topImpactData?.distinctnessData;
  const impactScoreIVKey = toImpactScoreIVKey({ valence, impactCode: impact });
  const percentile = impactScoreIVKey ? percentileMap?.get(impactScoreIVKey)?.percentile : undefined;

  const formattedValue = formatImpactScore(value, formatters, context.options.scoreUnit);
  const showFriendlyShortUnit = context.options.scoreUnit === FinalImpactScoreType.CENTS_PER_DOLLAR;
  const modalMode = layout.boxMode === LensBoxMode.MODAL;

  const showDrilldownLink =
    !modalMode &&
    modelResultId !== undefined &&
    !context.options.disableLinks &&
    isEnoughAccessToReadOutput(context.data?.accessLevel);

  const disableDrillDownLink = !isEnoughAccessToReadWithoutWeights(context.data?.accessLevel);

  return (
    <div>
      <GenericDynamicHeightInfoBox ref={ref} side={valenceToSide(valence)}>
        <InfoBoxTitleRow>
          <TitleMain style={{ justifyContent: modalMode ? 'start' : 'space-between' }}>
            <div>{friendlyShortImpactTitle(impact)}</div>
            <Box sx={{ fontSize: 12, color: valenceTextColorPrimer(valence), ml: modalMode ? 1 : undefined }}>
              {signForValenceAndValue(valence, value)}
              {formattedValue}
              {showFriendlyShortUnit && ' ¢/$'}
            </Box>
          </TitleMain>
        </InfoBoxTitleRow>
        <BoxMain>
          {!isNil(percentile) && (
            <Box sx={{ mb: 3, mt: 2, color: 'text.muted' }}>
              Out of the <FundBenchmarkUniverseCount /> funds in the Upright database,{' '}
              <strong>
                {formatPercentileIntoTopPercentByValence(percentile, valence)} have a{' '}
                {valence === Valence.NEGATIVE ? 'lower negative' : 'higher positive'} impact
              </strong>{' '}
              in this category.
            </Box>
          )}
          <SingleImpactContributors />
        </BoxMain>
        {showDrilldownLink && (
          <InfoBoxBottomPart
            left={
              <Link
                indicator="icon"
                to={
                  disableDrillDownLink
                    ? ''
                    : getDrilldownUrl({ ...entity, impactCode: impact, valence, resultId: modelResultId })
                }
                onClick={() => {
                  if (disableDrillDownLink) {
                    setShowNoAccess(true);
                  }
                  pushGtmEvent({
                    event: CustomGtmEvent.IMPACT_PROFILE_NI_DRILLDOWN_CLICK,
                    data: { impactCode: impact, valence },
                  });
                }}
                leadingIcon={ChevronRightIcon}
              >
                View detailed drilldown
              </Link>
            }
            right={<></>}
          />
        )}
      </GenericDynamicHeightInfoBox>
      {showNoAccess && <NoDrillDownAccess />}
    </div>
  );
}

function useClearStateOnOutsideClickRef(onResetState: () => void) {
  const context = useNetImpactLensContext();
  const carouselContext = useCarouselContext();

  const ref = useRef(null);

  const handleClickOutside = () => {
    if (context?.options.disableStateClearingOnOutsideClick) return;
    const newState = carouselContext.lens.getClearedSelectionState?.(carouselContext.selectionState);
    if (newState) {
      carouselContext.setSelectionState(newState);
    }
    onResetState();
  };

  useOnClickOutside([ref], () => {
    handleClickOutside();
  });
  return ref;
}

const BoxMain = styled.div`
  ${commonInfoBoxPadding};
`;

const TitleMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
